import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, useAuth } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import {
  Video,
  Youtube,
  Facebook,
  Instagram,
  Plus,
  Trash2,
  Edit,
  RefreshCw,
  Radio,
  Calendar,
  Clock,
  Star,
  ExternalLink,
  Play,
  Square,
  Link2
} from 'lucide-react';

const AdminLiveStreams = () => {
  const { token } = useAuth();
  const [streams, setStreams] = useState([]);
  const [socialLinks, setSocialLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showSocialDialog, setShowSocialDialog] = useState(false);
  const [editingStream, setEditingStream] = useState(null);
  const [activeTab, setActiveTab] = useState('streams');
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    platform: 'youtube',
    stream_url: '',
    thumbnail_url: '',
    is_live: false,
    is_featured: false,
    scheduled_date: '',
    scheduled_time: ''
  });

  const [socialForm, setSocialForm] = useState({
    platform: 'facebook',
    url: '',
    display_name: ''
  });

  const fetchStreams = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/streams`);
      setStreams(response.data);
    } catch (error) {
      console.error('Failed to fetch streams:', error);
    }
  }, []);

  const fetchSocialLinks = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/social-links`);
      setSocialLinks(response.data);
    } catch (error) {
      console.error('Failed to fetch social links:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStreams();
    fetchSocialLinks();
  }, [fetchStreams, fetchSocialLinks]);

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      platform: 'youtube',
      stream_url: '',
      thumbnail_url: '',
      is_live: false,
      is_featured: false,
      scheduled_date: '',
      scheduled_time: ''
    });
    setEditingStream(null);
  };

  const handleOpenEdit = (stream) => {
    setEditingStream(stream);
    setFormData({
      title: stream.title,
      description: stream.description || '',
      platform: stream.platform,
      stream_url: stream.stream_url,
      thumbnail_url: stream.thumbnail_url || '',
      is_live: stream.is_live,
      is_featured: stream.is_featured,
      scheduled_date: stream.scheduled_date || '',
      scheduled_time: stream.scheduled_time || ''
    });
    setShowDialog(true);
  };

  const handleSaveStream = async () => {
    if (!formData.title.trim() || !formData.stream_url.trim()) {
      toast.error('Title and Stream URL are required');
      return;
    }

    setSaving(true);
    try {
      if (editingStream) {
        await axios.put(
          `${API}/admin/streams/${editingStream.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Stream updated');
      } else {
        await axios.post(
          `${API}/admin/streams`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Stream created');
      }
      setShowDialog(false);
      resetForm();
      fetchStreams();
    } catch (error) {
      toast.error('Failed to save stream');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleLive = async (stream) => {
    try {
      await axios.patch(
        `${API}/admin/streams/${stream.id}/go-live?is_live=${!stream.is_live}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(stream.is_live ? 'Stream ended' : 'Stream is now LIVE!');
      fetchStreams();
    } catch (error) {
      toast.error('Failed to update stream');
    }
  };

  const handleDeleteStream = async (streamId) => {
    if (!confirm('Delete this stream?')) return;
    
    try {
      await axios.delete(`${API}/admin/streams/${streamId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Stream deleted');
      fetchStreams();
    } catch (error) {
      toast.error('Failed to delete stream');
    }
  };

  const handleSaveSocialLink = async () => {
    if (!socialForm.url.trim()) {
      toast.error('URL is required');
      return;
    }

    setSaving(true);
    try {
      const params = new URLSearchParams({
        platform: socialForm.platform,
        url: socialForm.url,
        display_name: socialForm.display_name || socialForm.platform
      });
      
      await axios.post(
        `${API}/admin/social-links?${params}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Social link saved');
      setShowSocialDialog(false);
      setSocialForm({ platform: 'facebook', url: '', display_name: '' });
      fetchSocialLinks();
    } catch (error) {
      toast.error('Failed to save social link');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSocialLink = async (platform) => {
    if (!confirm(`Delete ${platform} link?`)) return;
    
    try {
      await axios.delete(`${API}/admin/social-links/${platform}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Social link deleted');
      fetchSocialLinks();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const getPlatformIcon = (platform) => {
    switch (platform?.toLowerCase()) {
      case 'youtube': return <Youtube className="w-5 h-5 text-red-600" />;
      case 'facebook': return <Facebook className="w-5 h-5 text-blue-600" />;
      case 'instagram': return <Instagram className="w-5 h-5 text-pink-600" />;
      default: return <Video className="w-5 h-5" />;
    }
  };

  const getStatusBadge = (stream) => {
    if (stream.is_live) {
      return <Badge className="bg-red-500 text-white animate-pulse"><Radio className="w-3 h-3 mr-1" />LIVE</Badge>;
    }
    switch (stream.status) {
      case 'scheduled':
        return <Badge variant="outline"><Calendar className="w-3 h-3 mr-1" />Scheduled</Badge>;
      case 'ended':
        return <Badge className="bg-slate-500 text-white">Ended</Badge>;
      default:
        return <Badge variant="outline">{stream.status}</Badge>;
    }
  };

  return (
    <div className="p-4 sm:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Video className="w-5 h-5 sm:w-6 sm:h-6 text-red-500" /> Live Streams & Social
          </h1>
          <p className="text-sm sm:text-base text-slate-600">Manage live streams and social media links for Robyn Responds</p>
        </div>
        <Button variant="outline" onClick={() => { fetchStreams(); fetchSocialLinks(); }}>
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-white border">
          <TabsTrigger value="streams"><Video className="w-4 h-4 mr-1" /> Live Streams</TabsTrigger>
          <TabsTrigger value="social"><Link2 className="w-4 h-4 mr-1" /> Social Links</TabsTrigger>
        </TabsList>

        {/* Live Streams Tab */}
        <TabsContent value="streams" className="space-y-6 mt-6">
          <div className="flex justify-end">
            <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-plp-blue hover:bg-plp-blue-light" data-testid="add-stream-btn">
                  <Plus className="w-4 h-4 mr-2" /> Add Stream
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle className="font-heading">
                    {editingStream ? 'Edit Stream' : 'Add New Stream'}
                  </DialogTitle>
                </DialogHeader>
                
                <div className="space-y-4 mt-4">
                  <div>
                    <Label>Title *</Label>
                    <Input
                      value={formData.title}
                      onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="e.g., Weekly Porch Chat"
                      data-testid="stream-title"
                    />
                  </div>

                  <div>
                    <Label>Description</Label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="What's this stream about?"
                      rows={2}
                    />
                  </div>

                  <div>
                    <Label>Platform</Label>
                    <Select
                      value={formData.platform}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, platform: value }))}
                    >
                      <SelectTrigger data-testid="stream-platform">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="youtube"><Youtube className="w-4 h-4 inline mr-2" />YouTube</SelectItem>
                        <SelectItem value="facebook"><Facebook className="w-4 h-4 inline mr-2" />Facebook</SelectItem>
                        <SelectItem value="instagram"><Instagram className="w-4 h-4 inline mr-2" />Instagram</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Stream/Video URL *</Label>
                    <Input
                      value={formData.stream_url}
                      onChange={(e) => setFormData(prev => ({ ...prev, stream_url: e.target.value }))}
                      placeholder="https://youtube.com/watch?v=... or https://fb.watch/..."
                      data-testid="stream-url"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Paste the YouTube, Facebook, or Instagram live/video URL
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Scheduled Date</Label>
                      <Input
                        value={formData.scheduled_date}
                        onChange={(e) => setFormData(prev => ({ ...prev, scheduled_date: e.target.value }))}
                        placeholder="e.g., January 25, 2025"
                      />
                    </div>
                    <div>
                      <Label>Scheduled Time</Label>
                      <Input
                        value={formData.scheduled_time}
                        onChange={(e) => setFormData(prev => ({ ...prev, scheduled_time: e.target.value }))}
                        placeholder="e.g., 7:00 PM"
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div>
                      <Label className="text-base">Featured Stream</Label>
                      <p className="text-xs text-slate-500">Show prominently on Robyn Responds page</p>
                    </div>
                    <Switch
                      checked={formData.is_featured}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_featured: checked }))}
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg">
                    <div>
                      <Label className="text-base text-red-700">Go Live Now</Label>
                      <p className="text-xs text-red-600">Set this stream as currently live</p>
                    </div>
                    <Switch
                      checked={formData.is_live}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_live: checked, is_featured: checked ? true : prev.is_featured }))}
                    />
                  </div>

                  <div className="flex gap-3">
                    <Button variant="outline" onClick={() => { setShowDialog(false); resetForm(); }} className="flex-1">
                      Cancel
                    </Button>
                    <Button onClick={handleSaveStream} disabled={saving} className="flex-1 bg-plp-blue hover:bg-plp-blue-light" data-testid="save-stream-btn">
                      {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Video className="w-4 h-4 mr-2" />}
                      {editingStream ? 'Update' : 'Create'} Stream
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Streams List */}
          {loading ? (
            <div className="flex justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-plp-blue" />
            </div>
          ) : streams.length === 0 ? (
            <Card className="bg-white">
              <CardContent className="py-12 text-center">
                <Video className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No streams configured yet</p>
                <p className="text-sm text-slate-400 mt-2">Add a YouTube, Facebook, or Instagram live stream</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {streams.map((stream) => (
                <Card key={stream.id} className={`bg-white ${stream.is_live ? 'ring-2 ring-red-500' : ''}`} data-testid={`stream-${stream.id}`}>
                  <CardContent className="p-4 sm:p-6">
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                          {getPlatformIcon(stream.platform)}
                        </div>
                        <div>
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <h3 className="font-heading font-bold">{stream.title}</h3>
                            {getStatusBadge(stream)}
                            {stream.is_featured && <Badge className="bg-plp-gold text-plp-blue"><Star className="w-3 h-3 mr-1" />Featured</Badge>}
                          </div>
                          <p className="text-sm text-slate-600">{stream.description}</p>
                          <div className="flex flex-wrap items-center gap-4 mt-2 text-xs text-slate-400">
                            <span className="flex items-center gap-1">
                              {getPlatformIcon(stream.platform)}
                              {stream.platform}
                            </span>
                            {stream.scheduled_date && (
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {stream.scheduled_date}
                              </span>
                            )}
                            {stream.scheduled_time && (
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {stream.scheduled_time}
                              </span>
                            )}
                          </div>
                          <a href={stream.stream_url} target="_blank" rel="noopener noreferrer" className="text-xs text-plp-blue hover:underline mt-1 inline-flex items-center gap-1">
                            <ExternalLink className="w-3 h-3" />
                            View stream link
                          </a>
                        </div>
                      </div>

                      <div className="flex sm:flex-col gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleToggleLive(stream)}
                          className={stream.is_live ? 'bg-slate-600 hover:bg-slate-700' : 'bg-red-500 hover:bg-red-600'}
                          data-testid={`toggle-live-${stream.id}`}
                        >
                          {stream.is_live ? <><Square className="w-4 h-4 mr-1" />End</> : <><Play className="w-4 h-4 mr-1" />Go Live</>}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleOpenEdit(stream)}>
                          <Edit className="w-4 h-4 mr-1" />Edit
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDeleteStream(stream.id)} className="text-red-500 hover:bg-red-50">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Social Links Tab */}
        <TabsContent value="social" className="space-y-6 mt-6">
          <div className="flex justify-end">
            <Dialog open={showSocialDialog} onOpenChange={setShowSocialDialog}>
              <DialogTrigger asChild>
                <Button className="bg-plp-blue hover:bg-plp-blue-light" data-testid="add-social-btn">
                  <Plus className="w-4 h-4 mr-2" /> Add Social Link
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-heading">Add Social Link</DialogTitle>
                </DialogHeader>
                
                <div className="space-y-4 mt-4">
                  <div>
                    <Label>Platform</Label>
                    <Select
                      value={socialForm.platform}
                      onValueChange={(value) => setSocialForm(prev => ({ ...prev, platform: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="facebook">Facebook</SelectItem>
                        <SelectItem value="instagram">Instagram</SelectItem>
                        <SelectItem value="youtube">YouTube</SelectItem>
                        <SelectItem value="twitter">Twitter/X</SelectItem>
                        <SelectItem value="tiktok">TikTok</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Profile URL *</Label>
                    <Input
                      value={socialForm.url}
                      onChange={(e) => setSocialForm(prev => ({ ...prev, url: e.target.value }))}
                      placeholder="https://facebook.com/robynlyons"
                      data-testid="social-url"
                    />
                  </div>

                  <div>
                    <Label>Display Name</Label>
                    <Input
                      value={socialForm.display_name}
                      onChange={(e) => setSocialForm(prev => ({ ...prev, display_name: e.target.value }))}
                      placeholder="Robyn Lyons on Facebook"
                    />
                  </div>

                  <div className="flex gap-3">
                    <Button variant="outline" onClick={() => setShowSocialDialog(false)} className="flex-1">
                      Cancel
                    </Button>
                    <Button onClick={handleSaveSocialLink} disabled={saving} className="flex-1 bg-plp-blue hover:bg-plp-blue-light" data-testid="save-social-btn">
                      {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />}
                      Save Link
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Social Links List */}
          {socialLinks.length === 0 ? (
            <Card className="bg-white">
              <CardContent className="py-12 text-center">
                <Link2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No social links configured</p>
                <p className="text-sm text-slate-400 mt-2">Add links to Robyn's social media profiles</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {socialLinks.map((link) => (
                <Card key={link.platform} className="bg-white">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {getPlatformIcon(link.platform)}
                        <div>
                          <p className="font-medium">{link.display_name || link.platform}</p>
                          <a href={link.url} target="_blank" rel="noopener noreferrer" className="text-xs text-plp-blue hover:underline flex items-center gap-1">
                            <ExternalLink className="w-3 h-3" />
                            View Profile
                          </a>
                        </div>
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => handleDeleteSocialLink(link.platform)} className="text-red-500 hover:bg-red-50">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminLiveStreams;
